/**
 * LA RED SIN CONEXIÓN: cada LECTURA a la base que sale bien se guarda en el
 * teléfono, y cuando la red falla (o tarda con mala cobertura) se contesta con
 * la última copia en vez de con un error.
 *
 * POR QUÉ AQUÍ Y NO PANTALLA A PANTALLA. Media app pide sus datos con
 * `useEffect` + `supabase.from(...)` sin React Query, y `postgrest-js` no lanza
 * cuando la red se cae: devuelve `{ data: null, error, status: 0 }`, que casi
 * todas las pantallas pintan como «no hay nada» (ver `consultaConReintento`).
 * Engancharse debajo de `supabase-js` cubre TODAS las lecturas de golpe —las
 * 800 `.from()` y las RPC de lectura— sin tocar ninguna pantalla: lo que se vio
 * una vez con red se vuelve a ver sin ella.
 *
 * DOS ENGANCHES, porque hay dos momentos:
 *
 *  1. `window.fetch` (el de abajo). `supabase-js` resuelve `fetch` en cada
 *     petición (`lib/fetch.js → resolveFetch`), así que aquí llega la petición
 *     YA FIRMADA: se sabe de qué usuario es por su token. Aquí se guarda lo que
 *     llega bien y se contesta con la copia si la red revienta.
 *
 *  2. `supabase.rest.fetch` (el de arriba, ANTES de la firma). La firma espera a
 *     `auth.getSession()`, y con el token caducado y sin cobertura esa espera son
 *     ~30 s de reintentos de renovación. Sin red segura se contesta aquí mismo
 *     con la copia, sin esperar a nadie; con la red dudosa, se da a la red un
 *     margen corto y si no contesta se enseña la copia (la red sigue por detrás
 *     y deja la copia al día para la próxima vez).
 *
 * LO QUE NUNCA HACE:
 *   · guardar o repetir una ESCRITURA (POST/PATCH/DELETE a tablas, o una RPC que
 *     no esté en `RPC_DE_LECTURA`);
 *   · guardar una respuesta hecha SIN el usuario (con la clave anónima porque la
 *     renovación del token falló): la RLS la devuelve VACÍA y pisaría lo bueno;
 *   · leer lo de otro usuario: todo va por `usuarioOffline()`.
 */
import { supabase } from "@/integrations/supabase/client";
import { guardar, leer } from "./almacenOffline";
import { contestarConsulta, analizarConsulta, tablasNecesarias } from "./consultaLocal";
import { cargarTablas, filasEnMemoria } from "./espejoOffline";
import { CABECERA_ESPEJO } from "./marcaDescarga";
import {
  acabaPeticionDeUsuario,
  empiezaPeticionDeUsuario,
  enRafagaDeCopias,
  marcarEscritura,
  marcarRespuestaReal,
  marcarServidoGuardado,
  marcarTropiezoDeRed,
  modoActivo,
  redDudosa,
  sinRedSegura,
  usuarioOffline,
} from "./estadoOffline";

/* ─── Qué se puede guardar ───────────────────────────────────────────────── */

/**
 * RPC que SOLO LEEN. Una RPC no se puede repetir desde una copia a ciegas: si
 * escribe (confirmar un parte, borrar una tarea…) repetirla sería mentir. Toda
 * RPC nueva tiene que estar aquí o en `RPC_QUE_ESCRIBEN`; lo vigila
 * `redOffline.rpcs.guard.test.ts`.
 */
export const RPC_DE_LECTURA: ReadonlySet<string> = new Set([
  "_catalogo_cultivos_on",
  "_precio_pendiente_on",
  "_registro_mapa_off",
  "actividad_equipo",
  "catalogo_cultivos_activo",
  "crop_catalog_list",
  "crop_catalog_varieties",
  "crop_pest_zone_coverage",
  "crop_pest_zone_verdicts",
  "get_block_assignments",
  "get_block_cost_metrics",
  "get_block_phi_status",
  "get_crop_costs",
  "get_cycle_irm_summary",
  "get_irm_overview",
  "get_perito_linked_companies",
  "get_team_member_emails",
  "is_first_admin",
  "location_pest_zones",
  "marca_informes",
  "perito_sin_empresa",
  "pest_crop_ai_activo",
  "pest_crop_lists_activo",
  "pest_zones_activo",
  "phi_por_cultivo",
  "precio_pendiente_activo",
  "terminal_activo",
  "terminal_en_aparato",
]);

/**
 * RPC que escriben, o que deciden algo en el momento y no valen de una copia
 * (`check_user_access` da paso al entrar; `check_pesticide_limit` valida un
 * tratamiento antes de guardarlo). Se dejan pasar tal cual, sin guardar nada.
 */
export const RPC_QUE_ESCRIBEN: ReadonlySet<string> = new Set([
  "admin_merge_inventory_products",
  "apply_cuaderno_change",
  "associate_crop_pests",
  "associate_own_crop_pests",
  "attach_scanned_invoice_file",
  "cancel_my_product_request",
  "check_pesticide_limit",
  "check_user_access",
  "create_treatment_with_components",
  "delete_planner_task",
  "ensure_cuaderno_draft",
  "link_session_to_focus",
  "notify_worker_quick_report",
  "pest_focus_action",
  "reavisar_falta_de_stock",
  "replace_treatment_components",
  "request_product_purchase",
  "resolve_login_email_by_username",
  "rpc_create_or_restock_product",
  "rpc_delete_or_archive_product",
  "rpc_set_pending_purchase_prices",
  "send_test_notification_to_user",
  "set_block_assignments",
  "set_location_zone",
  "set_session_assessment",
  "switch_perito_company",
  // Terminal de la finca (30-10-2026): gestionarla y vincular la tablet.
  "terminal_borrar",
  "terminal_desvincular",
  "terminal_entrar",
  "terminal_guardar",
  "terminal_producto_nuevo",
  "terminal_salir",
  "terminal_set_acceso",
  "terminal_set_login",
  "terminal_set_pin",
  "terminal_soltar",
  "terminal_vincular",
  "update_user_role",
]);

const SUPABASE_URL: string = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
const BASE_SUPABASE = SUPABASE_URL ? `${SUPABASE_URL.replace(/\/$/, "")}/` : "";
const BASE_REST = SUPABASE_URL ? `${SUPABASE_URL.replace(/\/$/, "")}/rest/v1/` : "";

/** Respuestas más grandes que esto no se guardan (no caben bien en IndexedDB de un móvil). */
const TOPE_CUERPO = 20 * 1024 * 1024;
/** Con la red dudosa, cuánto se espera a la red antes de enseñar la copia. */
export const ESPERA_RED_DUDOSA_MS = 2500;

export interface Peticion {
  /** Clave completa (método, ruta con filtros, cabeceras que cambian la respuesta, cuerpo). */
  clave: string;
  /** Clave corta para IndexedDB. */
  hash: string;
  metodo: string;
  /** Lo que va detrás de `/rest/v1/`: `tabla?select=...&col=eq.x` o `rpc/nombre`. */
  ruta: string;
  accept: string;
  prefer: string;
  range: string;
}

export interface RespuestaGuardada {
  k: string;
  at: number;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string | null;
}

/* ─── Utilidades ─────────────────────────────────────────────────────────── */

/** cyrb53: hash rápido de 53 bits, de sobra para no chocar entre unas miles de claves. */
export function hash53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function urlDe(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
}

function cabecera(input: RequestInfo | URL, init: RequestInit | undefined, nombre: string): string {
  const h = init?.headers;
  try {
    if (h) {
      if (typeof Headers !== "undefined" && h instanceof Headers) return h.get(nombre) ?? "";
      if (Array.isArray(h)) {
        const par = h.find(([k]) => k.toLowerCase() === nombre.toLowerCase());
        return par ? String(par[1]) : "";
      }
      const rec = h as Record<string, string>;
      for (const k of Object.keys(rec)) if (k.toLowerCase() === nombre.toLowerCase()) return String(rec[k]);
      return "";
    }
    if (typeof Request !== "undefined" && input instanceof Request) return input.headers.get(nombre) ?? "";
  } catch {
    /* nada */
  }
  return "";
}

/**
 * ¿Es una lectura a la base que se puede guardar? Devuelve su clave, o null.
 * Exportada para las pruebas.
 */
export function describirPeticion(input: RequestInfo | URL, init?: RequestInit, base = BASE_REST): Peticion | null {
  if (!base) return null;
  const url = urlDe(input);
  if (!url.startsWith(base)) return null;
  const ruta = url.slice(base.length);
  const metodo = (init?.method ?? (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET")).toUpperCase();

  let cuerpo = "";
  if (ruta.startsWith("rpc/")) {
    const nombre = ruta.slice(4).split(/[?/]/)[0];
    if (!RPC_DE_LECTURA.has(nombre)) return null;
    if (metodo !== "POST" && metodo !== "GET" && metodo !== "HEAD") return null;
    const b = init?.body;
    if (b != null && typeof b !== "string") return null;
    cuerpo = (b as string | undefined) ?? "";
  } else if (metodo !== "GET" && metodo !== "HEAD") {
    return null;
  }

  const accept = cabecera(input, init, "Accept");
  const prefer = cabecera(input, init, "Prefer");
  const range = cabecera(input, init, "Range");
  const clave = [
    metodo,
    ruta,
    `a=${accept}`,
    `p=${prefer}`,
    `s=${cabecera(input, init, "Accept-Profile")}${cabecera(input, init, "Content-Profile")}`,
    `r=${range}`,
    `b=${cuerpo}`,
  ].join("\n");
  return { clave, hash: hash53(clave), metodo, ruta, accept, prefer, range };
}

/** ¿Es una petición de la descarga completa? (ver `espejoOffline`) */
export function esDeLaDescarga(input: RequestInfo | URL, init?: RequestInit): boolean {
  return cabecera(input, init, CABECERA_ESPEJO) !== "";
}

/** La misma petición sin la marca de la descarga (no debe salir a la red: CORS). */
function sinMarca(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
  if (!init?.headers) return init;
  const h = new Headers(init.headers as HeadersInit);
  h.delete(CABECERA_ESPEJO);
  return { ...init, headers: h };
}

/** El usuario del token de una petición (`sub`), o null si va con la clave anónima. */
let ultimoToken = "";
let ultimoUid: string | null = null;
export function uidDelToken(authorization: string): string | null {
  const token = authorization.replace(/^Bearer\s+/i, "");
  if (!token) return null;
  if (token === ultimoToken) return ultimoUid;
  let uid: string | null = null;
  try {
    const parte = token.split(".")[1];
    if (parte) {
      const json = atob(parte.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(parte.length / 4) * 4, "="));
      const carga = JSON.parse(json) as { sub?: string; role?: string };
      uid = carga.role === "anon" ? null : carga.sub ?? null;
    }
  } catch {
    uid = null;
  }
  ultimoToken = token;
  ultimoUid = uid;
  return uid;
}

function esCancelacion(e: unknown): boolean {
  return !!e && typeof e === "object" && (e as { name?: string }).name === "AbortError";
}

export function construirRespuesta(c: RespuestaGuardada): Response {
  const headers = new Headers(c.headers);
  headers.set("x-ulture-offline", String(c.at));
  return new Response(c.body, { status: c.status, statusText: c.statusText, headers });
}

export async function leerRespuesta(uid: string, p: Peticion): Promise<RespuestaGuardada | null> {
  const c = await leer<RespuestaGuardada>("respuestas", uid, p.hash);
  // El hash es corto: se comprueba la clave entera para no dar una respuesta por otra.
  return c && c.k === p.clave ? c : null;
}

async function guardarRespuesta(uid: string, p: Peticion, resp: Response): Promise<void> {
  try {
    const body = resp.status === 204 ? null : await resp.text();
    if (body && body.length > TOPE_CUERPO) return;
    const headers: Record<string, string> = {};
    for (const n of ["content-type", "content-range", "preference-applied"]) {
      const v = resp.headers.get(n);
      if (v) headers[n] = v;
    }
    const valor: RespuestaGuardada = {
      k: p.clave,
      at: Date.now(),
      status: resp.status,
      statusText: resp.statusText,
      headers,
      body,
    };
    await guardar("respuestas", uid, p.hash, valor);
  } catch {
    /* sin sitio o sin IndexedDB: se sigue sin guardar */
  }
}

/**
 * La consulta contestada con la COPIA COMPLETA (`consultaLocal` sobre
 * `espejoOffline`), con la hora de la copia más vieja que usó. Null si no se
 * puede (RPC, tabla sin copia, sintaxis que el evaluador no entiende).
 */
async function contestarConLaCopiaCompleta(p: Peticion): Promise<RespuestaGuardada | null> {
  if (p.ruta.startsWith("rpc/")) return null;
  let tablas: string[];
  try {
    tablas = tablasNecesarias(analizarConsulta(p.ruta));
  } catch {
    return null;
  }
  const at = await cargarTablas(tablas);
  if (at === null) return null;
  const r = contestarConsulta(p.ruta, { metodo: p.metodo, accept: p.accept, prefer: p.prefer, range: p.range }, filasEnMemoria);
  if (!r) return null;
  return { k: p.clave, at, status: r.status, statusText: r.status === 200 ? "OK" : "", headers: r.headers, body: r.body };
}

/**
 * Sin red útil: lo más reciente entre la última respuesta exacta de ESTA
 * consulta y la copia completa. La exacta es la más fiel (la calculó el
 * servidor), pero puede ser vieja o no existir si la pantalla nunca se abrió;
 * la copia completa contesta cualquier cosa con lo de la última descarga.
 */
async function contestarConCopia(uid: string, p: Peticion): Promise<Response | null> {
  const [exacta, completa] = await Promise.all([leerRespuesta(uid, p), contestarConLaCopiaCompleta(p)]);
  const elegida = exacta && (!completa || exacta.at > completa.at) ? exacta : completa ?? exacta;
  if (!elegida) return null;
  marcarServidoGuardado(elegida.at);
  return construirRespuesta(elegida);
}

/* ─── Enganche 1: window.fetch (la petición ya firmada) ──────────────────── */

type Fetch = typeof fetch;

/** ¿Es una escritura de la app a Supabase? (tablas, RPC que escriben, funciones, subidas) */
export function esEscrituraASupabase(input: RequestInfo | URL, init?: RequestInit, raiz = BASE_SUPABASE): boolean {
  if (!raiz) return false;
  const url = urlDe(input);
  if (!url.startsWith(raiz)) return false;
  const metodo = (init?.method ?? (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET")).toUpperCase();
  if (metodo === "GET" || metodo === "HEAD" || metodo === "OPTIONS") return false;
  const ruta = url.slice(raiz.length);
  // Renovar el token o entrar NO es un cambio de datos: no puede apagar las copias del arranque.
  if (ruta.startsWith("auth/")) return false;
  if (ruta.startsWith("rest/v1/rpc/")) return !RPC_DE_LECTURA.has(ruta.slice("rest/v1/rpc/".length).split(/[?/]/)[0]);
  return ruta.startsWith("rest/v1/") || ruta.startsWith("functions/") || ruta.startsWith("storage/");
}

export function crearFetchOffline(fetchOriginal: Fetch, base = BASE_REST, raiz = BASE_SUPABASE): Fetch {
  return async function fetchOffline(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // La marca de la descarga nunca sale a la red (CORS), ni con el interruptor apagado.
    if (esDeLaDescarga(input, init)) return fetchOriginal(input, sinMarca(input, init));
    // Apagado (interruptor de la empresa): exactamente como antes.
    if (!modoActivo()) return fetchOriginal(input, init);
    const aSupabase = !!raiz && urlDe(input).startsWith(raiz);
    if (!aSupabase) return fetchOriginal(input, init);

    if (esEscrituraASupabase(input, init, raiz)) marcarEscritura();
    empiezaPeticionDeUsuario();
    try {
      return await atender(input, init);
    } finally {
      acabaPeticionDeUsuario();
    }
  };

  async function atender(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const p = describirPeticion(input, init, base);
    const uid = usuarioOffline();
    if (!p || !uid) {
      /* Cualquier otra petición a Supabase (renovar el token, Storage, funciones)
         que falle por red también dice «no hay red útil»: así se entra con lo
         guardado sin esperar a que la renovación agote sus reintentos. */
      try {
        return await fetchOriginal(input, init);
      } catch (e) {
        if (!esCancelacion(e)) marcarTropiezoDeRed();
        throw e;
      }
    }

    const deEsteUsuario = uidDelToken(cabecera(input, init, "Authorization")) === uid;

    let resp: Response;
    try {
      resp = await fetchOriginal(input, init);
    } catch (e) {
      if (esCancelacion(e)) throw e;
      marcarTropiezoDeRed();
      const copia = await contestarConCopia(uid, p);
      if (copia) return copia;
      throw e;
    }

    if (resp.ok) {
      if (deEsteUsuario) {
        marcarRespuestaReal();
        void guardarRespuesta(uid, p, resp.clone());
        return resp;
      }
      /* Con sesión pero firmada con la clave anónima: la renovación del token
         falló y la RLS contesta vacío. La copia vale más que ese vacío. */
      return (await contestarConCopia(uid, p)) ?? resp;
    }

    /* Pasarela caída (502/503/504) o token rechazado: también es «no hay red útil». */
    if (resp.status >= 500 || resp.status === 401) {
      marcarTropiezoDeRed();
      const copia = await contestarConCopia(uid, p);
      if (copia) return copia;
    }
    return resp;
  }
}

/* ─── Enganche 2: supabase.rest.fetch (antes de la firma) ─────────────────── */

export function crearRestFetchOffline(restFetchOriginal: Fetch, base = BASE_REST): Fetch {
  return async function restFetchOffline(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Apagado (interruptor de la empresa): exactamente como antes.
    if (!modoActivo()) return restFetchOriginal(input, init);
    if (esDeLaDescarga(input, init)) return restFetchOriginal(input, init);
    const p = describirPeticion(input, init, base);
    const uid = usuarioOffline();
    if (!p || !uid) return restFetchOriginal(input, init);

    /* Sin red segura: ni se intenta. La firma esperaría ~30 s a renovar el token. */
    if (sinRedSegura()) {
      const copia = await contestarConCopia(uid, p);
      if (copia) return copia;
      throw new TypeError("Failed to fetch (sin conexión)");
    }

    if (!redDudosa()) return restFetchOriginal(input, init);

    /* Ya se está enseñando lo guardado y no ha llegado nada de verdad desde
       entonces: la copia YA, sin el margen (en cadena —perfil, rol, empresa…—
       cada margen sumaba). La red sigue por detrás y deja la copia al día. */
    if (enRafagaDeCopias()) {
      const copia = await contestarConCopia(uid, p);
      if (copia) {
        void restFetchOriginal(input, init).catch(() => undefined);
        return copia;
      }
    }

    /* Red dudosa: se le da un margen corto; si no contesta y hay copia, la copia.
       La petición sigue por detrás y deja la copia al día (enganche 1). */
    const red = restFetchOriginal(input, init);
    return new Promise<Response>((resolve, reject) => {
      let hecho = false;
      const reloj = setTimeout(() => {
        void contestarConCopia(uid, p).then((c) => {
          if (hecho || !c) return;
          hecho = true;
          resolve(c);
        });
      }, ESPERA_RED_DUDOSA_MS);
      red.then(
        (r) => {
          clearTimeout(reloj);
          if (hecho) return;
          hecho = true;
          resolve(r);
        },
        (e) => {
          clearTimeout(reloj);
          if (hecho) return;
          hecho = true;
          reject(e);
        },
      );
    });
  };
}

/* ─── Instalación ─────────────────────────────────────────────────────────── */

let instalada = false;

/**
 * Se llama una vez al arrancar (`main.tsx`), antes de pintar. Idempotente.
 * Devuelve qué enganches quedaron puestos, para la prueba de arranque.
 */
export function instalarRedOffline(): { fetch: boolean; rest: boolean } {
  if (typeof window === "undefined" || !BASE_REST) return { fetch: false, rest: false };
  if (instalada) return { fetch: true, rest: true };
  instalada = true;

  const fetchOriginal = window.fetch.bind(window);
  window.fetch = crearFetchOffline(fetchOriginal);

  let rest = false;
  const cliente = supabase as unknown as { rest?: { fetch?: Fetch } };
  if (cliente.rest && typeof cliente.rest.fetch === "function") {
    cliente.rest.fetch = crearRestFetchOffline(cliente.rest.fetch);
    rest = true;
  }
  return { fetch: true, rest };
}
